/**
 * Proposals — the decision engine (docs/01 §4).
 *
 * ONE generic mechanism serving guest visits, purchases, time off and anything
 * else. Nothing in here knows what a guest visit is; it knows about quorums,
 * vetoes, deadlines and a decision log. That generality is the whole point:
 * §4.4 falls out for free.
 *
 * ── Purity contract ─────────────────────────────────────────────────────────
 * Every function here is pure. No `Date.now()`, no globals, no mutation of
 * inputs. `now` is always an explicit UTC epoch millisecond argument, because
 * the tablet's clock is untrusted (docs/02 §4) and because two offline devices
 * must independently compute identical results with no coordination.
 *
 * ── How resolution actually works ───────────────────────────────────────────
 * Resolution is NOT a state machine that mutates as votes arrive. It is a
 * function over an event timeline: every way a proposal can be decided has a
 * timestamp, and the decision is the EARLIEST such event that has already
 * happened by `now`. Four candidate events exist:
 *
 *     R  the moment enough rejections landed to make approval impossible
 *     U  the moment enough approvals landed to satisfy the quorum
 *     A  autoApproveAt          (null for money — money NEVER auto-approves)
 *     E  an optional hard expiry
 *
 * Formulating it this way buys four invariants for free, all property-tested:
 *
 *   1. ORDER INDEPENDENCE — votes may be applied in any order (they arrive out
 *      of order from three offline devices) and the outcome is identical.
 *   2. MONOTONICITY IN TIME — once the outcome leaves 'pending' at some `now`,
 *      it is identical for every later `now`.
 *   3. LATE VOTES ARE INERT — a vote cast after the decision instant can never
 *      change the outcome. A reject that arrives at hour 5 does not un-approve
 *      something that auto-approved at hour 4.
 *   4. IDEMPOTENCE — settling an already-settled proposal is the identity.
 */

import type { MemberId, ProposalId } from './ids.js'
import type { Settings } from './entities/misc.js'
import type {
  DecisionReason,
  Proposal,
  ProposalOutcome,
  ProposalStatus,
  ProposalType,
  ProposalVote,
  Quorum,
  Vote,
} from './entities/proposals.js'
import { DEFAULT_ROLLOVER_HOUR, rolloverDayOf, type DayKey } from './time.js'

const MS_PER_HOUR = 3_600_000
const MS_PER_MINUTE = 60_000

// ───────────────────────────────────────────────────────────────────────────
// 1. Built-in proposal types
// ───────────────────────────────────────────────────────────────────────────

/** All three owners vote on everything, so the default deadline is 4 hours. */
export const DEFAULT_AUTO_APPROVE_HOURS = 4

/** Reminder push this long before `autoApproveAt` (docs/01 §4.2). */
export const DEFAULT_REMINDER_LEAD_MINUTES = 30

/**
 * Default purchase threshold: 100 EGP, expressed in INTEGER PIASTRES.
 * Lives in Settings and is editable from the front end — never hardcoded at a
 * call site. Below it, no proposal is needed at all.
 */
export const DEFAULT_PURCHASE_THRESHOLD_PIASTRES = 10_000

/**
 * The five built-in types (docs/01 §4.1). Every one is unanimous among all
 * three owners; only `purchase` is money-related, and money never auto-approves
 * however long it sits.
 */
export const BUILT_IN_PROPOSAL_TYPES = {
  guest_visit: {
    key: 'guest_visit',
    label: 'Guest visit',
    icon: 'user-plus',
    quorum: 'unanimous',
    autoApproveAfterHours: DEFAULT_AUTO_APPROVE_HOURS,
    isMoneyRelated: false,
  },
  room_exception: {
    key: 'room_exception',
    label: 'Room exception',
    icon: 'door-open',
    quorum: 'unanimous',
    autoApproveAfterHours: DEFAULT_AUTO_APPROVE_HOURS,
    isMoneyRelated: false,
  },
  time_off: {
    key: 'time_off',
    label: 'Time off',
    icon: 'palm-tree',
    quorum: 'unanimous',
    autoApproveAfterHours: DEFAULT_AUTO_APPROVE_HOURS,
    isMoneyRelated: false,
  },
  generic: {
    key: 'generic',
    label: 'Proposal',
    icon: 'message-square',
    quorum: 'unanimous',
    autoApproveAfterHours: DEFAULT_AUTO_APPROVE_HOURS,
    isMoneyRelated: false,
  },
  purchase: {
    key: 'purchase',
    label: 'Purchase',
    icon: 'banknote',
    quorum: 'unanimous',
    /** NEVER auto-approves. Money waits for a physical tap (docs/01 §4.1). */
    autoApproveAfterHours: null,
    isMoneyRelated: true,
  },
} as const satisfies Record<string, ProposalType>

export type BuiltInProposalTypeKey = keyof typeof BUILT_IN_PROPOSAL_TYPES

export const BUILT_IN_PROPOSAL_TYPE_KEYS = Object.keys(
  BUILT_IN_PROPOSAL_TYPES,
) as BuiltInProposalTypeKey[]

/** A lookup from `typeKey` to its configuration. Types are editable in settings. */
export type ProposalTypeRegistry = Readonly<Record<string, ProposalType>>

export const BUILT_IN_PROPOSAL_TYPE_REGISTRY: ProposalTypeRegistry = {
  ...BUILT_IN_PROPOSAL_TYPES,
}

/**
 * Build the live registry from settings, so the 4-hour window is configurable
 * without touching code. The money rule is re-asserted here rather than trusted:
 * `isMoneyRelated` forces `autoApproveAfterHours` to null no matter what the
 * stored row says.
 */
export function proposalTypesFromSettings(
  settings: Pick<Settings, 'autoApproveHours'>,
  extraTypes: readonly ProposalType[] = [],
): ProposalTypeRegistry {
  const out: Record<string, ProposalType> = {}
  for (const t of Object.values(BUILT_IN_PROPOSAL_TYPES)) {
    out[t.key] = {
      ...t,
      autoApproveAfterHours: t.isMoneyRelated ? null : settings.autoApproveHours,
    }
  }
  for (const t of extraTypes) {
    out[t.key] = {
      ...t,
      autoApproveAfterHours: t.isMoneyRelated ? null : t.autoApproveAfterHours,
    }
  }
  return out
}

export function proposalTypeOf(
  registry: ProposalTypeRegistry,
  typeKey: string,
): ProposalType | null {
  return registry[typeKey] ?? null
}

// ───────────────────────────────────────────────────────────────────────────
// 2. The purchase threshold
// ───────────────────────────────────────────────────────────────────────────

/**
 * Does this spend need a proposal at all?
 *
 * "Spending below it needs no proposal at all; at or above it, a proposal is
 * required" (docs/01 §4.1) — so the comparison is `>=`, not `>`.
 *
 * Both arguments are INTEGER PIASTRES. Floats are rejected loudly rather than
 * rounded silently: a float creeping into money is exactly the class of bug the
 * integer-piastres rule exists to prevent. Magnitude is what matters, so a
 * refund of -500 EGP is treated like a spend of 500 EGP.
 */
export function purchaseNeedsProposal(amountPiastres: number, thresholdPiastres: number): boolean {
  assertIntegerPiastres(amountPiastres, 'amountPiastres')
  assertIntegerPiastres(thresholdPiastres, 'thresholdPiastres')
  return Math.abs(amountPiastres) >= Math.abs(thresholdPiastres)
}

/** Convenience wrapper reading the (editable) threshold out of settings. */
export function purchaseNeedsProposalPerSettings(
  amountPiastres: number,
  settings: Pick<Settings, 'purchaseThreshold'>,
): boolean {
  return purchaseNeedsProposal(amountPiastres, settings.purchaseThreshold)
}

function assertIntegerPiastres(n: number, label: string): void {
  if (!Number.isInteger(n)) {
    throw new RangeError(`${label} must be an integer number of piastres, got ${String(n)}`)
  }
}

// ───────────────────────────────────────────────────────────────────────────
// 3. Creating proposals
// ───────────────────────────────────────────────────────────────────────────

export interface CreateProposalInput {
  id: ProposalId
  type: ProposalType
  createdBy: MemberId
  /** UTC epoch ms. Becomes `createdAt`, and the base for `autoApproveAt`. */
  now: number
  title: string
  details?: string | null
  payload?: Readonly<Record<string, unknown>>
  supersedesId?: ProposalId | null
}

/**
 * `autoApproveAt` for a type, or null when it must never auto-approve.
 *
 * The deadline is `createdAt + hours` in REAL milliseconds, deliberately not
 * "the same wall-clock time plus four". On the two days a year the Cairo offset
 * changes, wall-clock arithmetic would make the window 3 or 5 hours long.
 */
export function autoApproveAtFor(type: ProposalType, createdAt: number): number | null {
  if (type.isMoneyRelated) return null
  const hours = type.autoApproveAfterHours
  if (hours === null || !Number.isFinite(hours) || hours < 0) return null
  if (!Number.isFinite(createdAt)) return null
  const at = createdAt + hours * MS_PER_HOUR
  // An absurd window overflows to Infinity, which is not a storable instant:
  // JSON round-trips it to null anyway. Say "never" here rather than persisting
  // a value that means one thing in memory and another after a sync.
  return Number.isFinite(at) ? at : null
}

export function createProposal(input: CreateProposalInput): Proposal {
  return {
    id: input.id,
    typeKey: input.type.key,
    createdBy: input.createdBy,
    createdAt: input.now,
    title: input.title,
    details: input.details ?? null,
    // Copied, never aliased: the caller's object must not become live state.
    payload: { ...(input.payload ?? {}) },
    status: 'pending',
    autoApproveAt: autoApproveAtFor(input.type, input.now),
    reminderSentAt: null,
    decidedAt: null,
    decisionReason: null,
    supersedesId: input.supersedesId ?? null,
  }
}

// ───────────────────────────────────────────────────────────────────────────
// 4. Quorum arithmetic
// ───────────────────────────────────────────────────────────────────────────

/** Approvals required to carry a proposal. Unanimous = everyone. */
export function requiredApprovals(quorum: Quorum, voterCount: number): number {
  if (voterCount <= 0) return 0
  return quorum === 'unanimous' ? voterCount : Math.floor(voterCount / 2) + 1
}

/**
 * Rejections required to KILL a proposal — the point at which the quorum has
 * become arithmetically unreachable.
 *
 * Under unanimity this is 1, which is precisely "any reject resolves it
 * immediately" (docs/01 §4.2): there is no point waiting for the third vote
 * when one veto already decides it. Under majority it is correctly 2 of 3,
 * 2 of 4, 3 of 5. One formula, both quorums.
 *
 * Invariant: requiredApprovals + blockingRejections === voterCount + 1.
 */
export function blockingRejections(quorum: Quorum, voterCount: number): number {
  if (voterCount <= 0) return 0
  return voterCount - requiredApprovals(quorum, voterCount) + 1
}

// ───────────────────────────────────────────────────────────────────────────
// 5. Resolution
// ───────────────────────────────────────────────────────────────────────────

export interface ResolveOptions {
  /**
   * Quorum for this proposal. Resolution otherwise assumes 'unanimous', which
   * every built-in type is. Prefer passing `types` so it is looked up.
   */
  quorum?: Quorum
  /**
   * The live type registry. Supplying it lets resolution defend the money rule
   * independently of the stored row: if the type is money-related, a stray
   * non-null `autoApproveAt` (a bad migration, a hand-edited sync payload) is
   * ignored rather than obeyed.
   */
  types?: ProposalTypeRegistry
  /**
   * Optional hard expiry. Nothing built-in sets one; without it a money
   * proposal sits pending indefinitely, which is the documented behaviour.
   */
  expiresAt?: number | null
  /** Defaults to true — the proposer counts as an implicit approve. */
  proposerImplicitApprove?: boolean
}

/**
 * A FRESH pending outcome every time. Never a shared module-level constant: a
 * caller that writes to a returned outcome would otherwise poison every later
 * "still pending" answer in the process.
 */
function pendingOutcome(): ProposalOutcome {
  return { status: 'pending', reason: null, decidedAt: null }
}

interface EffectiveVote {
  voterId: MemberId
  vote: Vote
  /** Vote time, clamped so that a decision can never precede its proposal. */
  at: number
  implicit: boolean
}

/**
 * The tallying view of the votes.
 *
 * Three deliberate rules, each of which exists to keep resolution a pure,
 * order-independent function:
 *
 *  - Votes for other proposals, and votes from people who are not eligible
 *    voters, are ignored outright.
 *  - FIRST vote wins per voter. The schema has UNIQUE(proposal_id, voter_id),
 *    so duplicates only appear through sync replay; letting a later vote
 *    overwrite an earlier one would let a reject-then-approve sequence flip a
 *    settled outcome, which breaks monotonicity. On an exact timestamp tie the
 *    reject wins — a veto is the conservative reading.
 *  - The PROPOSER'S implicit approve is fixed at `createdAt` and an explicit
 *    vote row from the proposer does not displace it. Their route out of a
 *    proposal they no longer want is `cancel`, which §4.2 grants them
 *    explicitly. Letting them reject instead would mean a proposer's late vote
 *    could retroactively un-approve an already unanimous decision.
 */
function effectiveVotes(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligible: ReadonlySet<string>,
  proposerImplicit: boolean,
): Map<string, EffectiveVote> {
  const out = new Map<string, EffectiveVote>()
  for (const v of votes) {
    if (v.proposalId !== proposal.id) continue
    if (!eligible.has(v.voterId)) continue
    if (proposerImplicit && v.voterId === proposal.createdBy) continue
    const at = Math.max(v.votedAt, proposal.createdAt)
    const prev = out.get(v.voterId)
    if (prev === undefined || at < prev.at || (at === prev.at && v.vote === 'reject')) {
      out.set(v.voterId, { voterId: v.voterId, vote: v.vote, at, implicit: false })
    }
  }
  if (proposerImplicit && eligible.has(proposal.createdBy)) {
    out.set(proposal.createdBy, {
      voterId: proposal.createdBy,
      vote: 'approve',
      at: proposal.createdAt,
      implicit: true,
    })
  }
  return out
}

interface DecisionCandidate {
  at: number
  /** Tie-break rank when two candidates land on the same millisecond. */
  rank: number
  status: ProposalStatus
  reason: DecisionReason
}

function decisionCandidates(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  opts: ResolveOptions,
): DecisionCandidate[] {
  const eligible = uniqueIds(eligibleVoters)
  const eligibleSet = new Set<string>(eligible)
  const type = opts.types?.[proposal.typeKey]
  const quorum: Quorum = opts.quorum ?? type?.quorum ?? 'unanimous'
  const proposerImplicit = opts.proposerImplicitApprove ?? true

  const eff = effectiveVotes(proposal, votes, eligibleSet, proposerImplicit)
  const approveTimes: number[] = []
  const rejectTimes: number[] = []
  for (const id of eligible) {
    const v = eff.get(id)
    if (v === undefined) continue
    if (v.vote === 'approve') approveTimes.push(v.at)
    else rejectTimes.push(v.at)
  }
  approveTimes.sort(ascending)
  rejectTimes.sort(ascending)

  const n = eligible.length
  const needed = requiredApprovals(quorum, n)
  const blocking = blockingRejections(quorum, n)

  const candidates: DecisionCandidate[] = []

  // R — enough vetoes that the quorum can no longer be reached.
  if (n > 0 && blocking > 0 && rejectTimes.length >= blocking) {
    candidates.push({
      at: rejectTimes[blocking - 1]!,
      rank: 0,
      status: 'rejected',
      reason: 'rejected_by_vote',
    })
  }

  // U — the quorum is satisfied.
  // With no eligible voters, unanimity would be vacuously true; we refuse that
  // reading. Nobody eligible means nobody can carry it by vote.
  if (n > 0 && needed > 0 && approveTimes.length >= needed) {
    candidates.push({
      at: approveTimes[needed - 1]!,
      rank: 1,
      // The enum's only "approved by vote" reason. It reads as unanimity
      // because every built-in type IS unanimous; under a majority quorum it
      // means "the required quorum approved".
      status: 'approved',
      reason: 'unanimous_approval',
    })
  }

  // A — the 4-hour deadline. Never present for money.
  const moneyLocked = type?.isMoneyRelated === true
  const autoAt = moneyLocked ? null : proposal.autoApproveAt
  if (autoAt !== null && autoAt !== undefined && Number.isFinite(autoAt)) {
    candidates.push({
      at: autoAt,
      rank: 2,
      status: 'approved',
      reason: 'auto_approved_timeout',
    })
  }

  // E — optional hard expiry.
  const expiresAt = opts.expiresAt ?? null
  if (expiresAt !== null && Number.isFinite(expiresAt)) {
    candidates.push({ at: expiresAt, rank: 3, status: 'expired', reason: 'expired' })
  }

  return candidates
}

/**
 * Decide a proposal as of `now`.
 *
 * Already-decided proposals are returned unchanged, whatever the votes say:
 * the decision log must stay honest about what was actually agreed, and a
 * settled row is never re-litigated.
 */
export function resolve(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  now: number,
  opts: ResolveOptions = {},
): ProposalOutcome {
  if (proposal.status !== 'pending') {
    return {
      status: proposal.status,
      reason: proposal.decisionReason,
      decidedAt: proposal.decidedAt,
    }
  }

  const candidates = decisionCandidates(proposal, votes, eligibleVoters, opts).filter(
    c => c.at <= now,
  )
  if (candidates.length === 0) return pendingOutcome()

  // Earliest event wins; on an exact tie the ranks encode the precedence
  // veto > approved-by-vote > auto-approved > expired.
  candidates.sort((a, b) => a.at - b.at || a.rank - b.rank)
  const winner = candidates[0]!
  return { status: winner.status, reason: winner.reason, decidedAt: winner.at }
}

/** Write an outcome onto a proposal. Returns the SAME object when nothing changed. */
export function applyOutcome(proposal: Proposal, outcome: ProposalOutcome): Proposal {
  if (
    proposal.status === outcome.status &&
    proposal.decisionReason === outcome.reason &&
    proposal.decidedAt === outcome.decidedAt
  ) {
    return proposal
  }
  return {
    ...proposal,
    status: outcome.status,
    decisionReason: outcome.reason,
    decidedAt: outcome.decidedAt,
  }
}

/** resolve + applyOutcome. Idempotent: `settle(settle(p)) === settle(p)`. */
export function settle(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  now: number,
  opts: ResolveOptions = {},
): Proposal {
  return applyOutcome(proposal, resolve(proposal, votes, eligibleVoters, now, opts))
}

export function isDecided(outcome: Pick<ProposalOutcome, 'status'>): boolean {
  return outcome.status !== 'pending'
}

// ───────────────────────────────────────────────────────────────────────────
// 6. Cancellation
// ───────────────────────────────────────────────────────────────────────────

export type CancelFailure = 'not_proposer' | 'not_pending'

export type CancelResult =
  | { ok: true; proposal: Proposal }
  | { ok: false; reason: CancelFailure }

/**
 * The proposer may cancel while pending (docs/01 §4.2).
 *
 * Votes and voters are optional; supplying them makes the check exact by
 * settling first, so a proposal that has already reached unanimity cannot be
 * cancelled out from under the people who approved it.
 */
export function cancel(
  proposal: Proposal,
  byMemberId: MemberId,
  now: number,
  votes: readonly ProposalVote[] = [],
  eligibleVoters: readonly MemberId[] = [],
  opts: ResolveOptions = {},
): CancelResult {
  if (byMemberId !== proposal.createdBy) return { ok: false, reason: 'not_proposer' }
  const current = settle(proposal, votes, eligibleVoters, now, opts)
  if (current.status !== 'pending') return { ok: false, reason: 'not_pending' }
  return {
    ok: true,
    proposal: {
      ...current,
      status: 'cancelled',
      decisionReason: 'cancelled_by_proposer',
      // Clamped exactly as vote timestamps are: a device whose clock runs slow
      // must not be able to record a decision that precedes its own proposal.
      decidedAt: Math.max(now, proposal.createdAt),
    },
  }
}

export function canCancel(proposal: Proposal, byMemberId: MemberId): boolean {
  return proposal.status === 'pending' && proposal.createdBy === byMemberId
}

// ───────────────────────────────────────────────────────────────────────────
// 7. Reminders
// ───────────────────────────────────────────────────────────────────────────

export type ReminderReason =
  | 'due'
  | 'no_deadline'
  | 'not_pending'
  | 'already_sent'
  | 'too_early'
  | 'deadline_passed'

export interface ReminderCheck {
  /** True when a reminder push should go out right now. */
  due: boolean
  /** When the reminder window opens: `autoApproveAt - lead`. Null with no deadline. */
  dueAt: number | null
  /** Eligible voters with no vote recorded. The proposer is never awaited. */
  awaiting: MemberId[]
  reason: ReminderReason
}

/**
 * Should a reminder go out? (docs/01 §4.2)
 *
 * "Without it the 4-hour rule silently approves things nobody read." The window
 * is half-open: `[autoApproveAt - lead, autoApproveAt)`. Once the deadline has
 * passed the proposal auto-approves anyway, so a reminder is pointless and this
 * reports `deadline_passed` — which also keeps a once-a-minute sweep that was
 * down for an hour from firing a useless push on recovery.
 *
 * Money proposals have no `autoApproveAt`, so they never generate a timeout
 * reminder: there is no silent approval to warn anyone about. Pass `opts.types`
 * and that holds even for a corrupted row carrying a stray deadline.
 *
 * `votes` and `eligibleVoters` are optional and only affect `awaiting`; whether
 * anyone is left to nudge is the caller's decision, not this gate's.
 */
export function needsReminder(
  proposal: Proposal,
  now: number,
  leadMinutes: number = DEFAULT_REMINDER_LEAD_MINUTES,
  votes: readonly ProposalVote[] = [],
  eligibleVoters: readonly MemberId[] = [],
  opts: Pick<ResolveOptions, 'proposerImplicitApprove' | 'types'> = {},
): ReminderCheck {
  const awaiting = pendingVoters(proposal, votes, eligibleVoters, opts)
  // The same money lock resolution applies: if the registry says this type is
  // money-related, a stray stored deadline is ignored here too. Otherwise a
  // corrupted row would push "approving in 30 minutes" about something that
  // resolution will never auto-approve.
  const deadline =
    opts.types?.[proposal.typeKey]?.isMoneyRelated === true ? null : proposal.autoApproveAt
  // A garbage lead (NaN from a bad settings row) must not produce a NaN window,
  // which compares false against everything and so reads as "due right now".
  const lead = Number.isFinite(leadMinutes)
    ? Math.max(0, leadMinutes)
    : DEFAULT_REMINDER_LEAD_MINUTES
  const dueAt =
    deadline === null || !Number.isFinite(deadline) ? null : deadline - lead * MS_PER_MINUTE

  const fail = (reason: ReminderReason): ReminderCheck => ({ due: false, dueAt, awaiting, reason })

  if (proposal.status !== 'pending') return fail('not_pending')
  if (deadline === null || dueAt === null) return fail('no_deadline')
  if (proposal.reminderSentAt !== null) return fail('already_sent')
  if (now < dueAt) return fail('too_early')
  if (now >= deadline) return fail('deadline_passed')
  return { due: true, dueAt, awaiting, reason: 'due' }
}

/** Eligible voters who have not voted. The proposer's implicit approve counts. */
export function pendingVoters(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  opts: Pick<ResolveOptions, 'proposerImplicitApprove'> = {},
): MemberId[] {
  const eligible = uniqueIds(eligibleVoters)
  const eligibleSet = new Set<string>(eligible)
  const proposerImplicit = opts.proposerImplicitApprove ?? true
  const eff = effectiveVotes(proposal, votes, eligibleSet, proposerImplicit)
  return eligible.filter(id => !eff.has(id))
}

/** Record that the reminder went out. Never mutates the input. */
export function markReminderSent(proposal: Proposal, now: number): Proposal {
  if (proposal.reminderSentAt !== null) return proposal
  return { ...proposal, reminderSentAt: now }
}

// ───────────────────────────────────────────────────────────────────────────
// 8. The once-a-minute sweep (docs/02 §8)
// ───────────────────────────────────────────────────────────────────────────

export interface ReminderDue {
  proposal: Proposal
  awaiting: MemberId[]
}

export interface SweepResult {
  /** Proposals whose status changed. Already-settled ones are not repeated. */
  settled: Proposal[]
  /** Proposals needing a reminder push, with the voters to nudge. */
  reminders: ReminderDue[]
  /** Still pending after the sweep. */
  stillPending: Proposal[]
}

/**
 * One pass of the deadline sweep. Idempotent and stateless: running it twice
 * over the same inputs produces the same settlements and, once the reminder is
 * recorded via `markReminderSent`, no duplicate pushes.
 */
export function sweep(
  proposals: readonly Proposal[],
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  now: number,
  leadMinutes: number = DEFAULT_REMINDER_LEAD_MINUTES,
  opts: ResolveOptions = {},
): SweepResult {
  const byProposal = groupVotes(votes)
  const settled: Proposal[] = []
  const reminders: ReminderDue[] = []
  const stillPending: Proposal[] = []

  for (const p of proposals) {
    const vs = byProposal.get(p.id) ?? []
    const next = settle(p, vs, eligibleVoters, now, opts)
    if (next !== p) settled.push(next)
    if (next.status !== 'pending') continue
    stillPending.push(next)
    const check = needsReminder(next, now, leadMinutes, vs, eligibleVoters, opts)
    if (check.due) reminders.push({ proposal: next, awaiting: check.awaiting })
  }
  return { settled, reminders, stillPending }
}

/** Proposals still waiting on a specific member's tap. Drives the wall board. */
export function awaitingVoteFrom(
  memberId: MemberId,
  proposals: readonly Proposal[],
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  now: number,
  opts: ResolveOptions = {},
): Proposal[] {
  const byProposal = groupVotes(votes)
  const out: Proposal[] = []
  for (const p of proposals) {
    const vs = byProposal.get(p.id) ?? []
    const next = settle(p, vs, eligibleVoters, now, opts)
    if (next.status !== 'pending') continue
    if (pendingVoters(next, vs, eligibleVoters, opts).includes(memberId)) out.push(next)
  }
  return out
}

// ───────────────────────────────────────────────────────────────────────────
// 9. Counter-proposals (docs/01 §4.3)
// ───────────────────────────────────────────────────────────────────────────

/** An alternative carried by a vote — "no, but after 4pm works". */
export interface CounterProposal {
  proposalId: ProposalId
  voterId: MemberId
  vote: Vote
  payload: Record<string, unknown>
  comment: string | null
  votedAt: number
}

/** Counter-proposals attached to a proposal, oldest first. */
export function counterProposalsOf(
  proposal: Proposal,
  votes: readonly ProposalVote[],
): CounterProposal[] {
  const out: CounterProposal[] = []
  for (const v of votes) {
    if (v.proposalId !== proposal.id) continue
    if (v.counterProposal === null) continue
    out.push({
      proposalId: v.proposalId,
      voterId: v.voterId,
      vote: v.vote,
      payload: { ...v.counterProposal },
      comment: v.comment,
      votedAt: v.votedAt,
    })
  }
  out.sort((a, b) => a.votedAt - b.votedAt || compareStr(a.voterId, b.voterId))
  return out
}

export interface AcceptCounterProposalInput {
  original: Proposal
  counter: CounterProposal
  /** Fresh UUIDv7 for the new proposal. */
  id: ProposalId
  /** Whoever accepted — normally the original proposer. Gets the implicit approve. */
  createdBy: MemberId
  now: number
  type: ProposalType
  title?: string
  details?: string | null
  /**
   * 'merge' (default) layers the counter's payload over the original's, so a
   * counter that changes only the start time keeps the guest, the room and
   * everything else. 'replace' takes the counter's payload verbatim.
   */
  payloadMode?: 'merge' | 'replace'
}

/**
 * Accept a counter-proposal.
 *
 * Creates a NEW proposal linked via `supersedesId` and returns it. The original
 * is never touched — not its payload, not its status, not its decision reason.
 * That is the entire point of §4.3: the decision log has to stay honest about
 * what was actually proposed and what was actually agreed. Mutating the
 * original would rewrite history so that the rejected version never existed.
 *
 * The new proposal starts pending with a fresh `autoApproveAt` measured from
 * `now`, so accepting a counter restarts the 4-hour clock rather than
 * inheriting the original's expired one.
 */
export function acceptCounterProposal(input: AcceptCounterProposalInput): Proposal {
  const { original, counter } = input
  const payload =
    (input.payloadMode ?? 'merge') === 'replace'
      ? { ...counter.payload }
      : { ...original.payload, ...counter.payload }

  return createProposal({
    id: input.id,
    type: input.type,
    createdBy: input.createdBy,
    now: input.now,
    title: input.title ?? original.title,
    details: input.details !== undefined ? input.details : (counter.comment ?? original.details),
    payload,
    supersedesId: original.id,
  })
}

/**
 * The supersession chain ending at `id`, oldest first.
 * Cycle-safe: a corrupted `supersedesId` loop terminates rather than hanging.
 */
export function proposalChain(
  proposals: readonly Proposal[],
  id: ProposalId,
): Proposal[] {
  const index = new Map<string, Proposal>()
  for (const p of proposals) index.set(p.id, p)
  const chain: Proposal[] = []
  const seen = new Set<string>()
  let cursor: string | null = id
  while (cursor !== null && !seen.has(cursor)) {
    seen.add(cursor)
    const p: Proposal | undefined = index.get(cursor)
    if (p === undefined) break
    chain.push(p)
    cursor = p.supersedesId
  }
  chain.reverse()
  return chain
}

/** The proposal that supersedes `id`, if any has been raised. */
export function supersededBy(
  proposals: readonly Proposal[],
  id: ProposalId,
): Proposal | null {
  let best: Proposal | null = null
  for (const p of proposals) {
    if (p.supersedesId !== id) continue
    if (best === null || p.createdAt < best.createdAt || (p.createdAt === best.createdAt && compareStr(p.id, best.id) < 0)) {
      best = p
    }
  }
  return best
}

// ───────────────────────────────────────────────────────────────────────────
// 10. The decision log (docs/01 §4.4)
// ───────────────────────────────────────────────────────────────────────────

export type DecisionEventKind = 'proposed' | 'voted' | 'reminded' | 'decided'

export interface DecisionLogEvent {
  /** Position in the chronological log. Stable for a given input set. */
  seq: number
  at: number
  /** The office day this happened on, in 05:00-rollover reckoning. */
  day: DayKey
  kind: DecisionEventKind
  proposalId: ProposalId
  typeKey: string
  title: string
  /** Null for system events: an auto-approval and an expiry have no actor. */
  actorId: MemberId | null
  vote: Vote | null
  comment: string | null
  counterProposal: Record<string, unknown> | null
  /** Set on 'decided' events. */
  status: ProposalStatus | null
  reason: DecisionReason | null
  supersedesId: ProposalId | null
}

export interface DecisionLogOptions {
  /** Office day boundary. Defaults to 05:00 (docs/01). */
  rolloverHour?: number
  /**
   * Who was entitled to vote. Only affects attribution on `decided` events:
   * without it a row from someone who was never eligible (an intern, a member
   * removed from the pool) can be named as the cause of a decision their vote
   * did not actually cause. Supply it whenever the caller knows it.
   */
  eligibleVoters?: readonly MemberId[]
  /** Mirrors ResolveOptions. Defaults to true — the proposer implicitly approves. */
  proposerImplicitApprove?: boolean
}

const KIND_RANK: Record<DecisionEventKind, number> = {
  proposed: 0,
  voted: 1,
  reminded: 2,
  decided: 3,
}

/**
 * The permanent, chronological, queryable record: who proposed what, who voted
 * which way and when, and why each resolved.
 *
 * The ordering is a total order (time, then kind, then proposal id, then actor,
 * then vote), so the log is byte-identical on every device regardless of the
 * order rows arrived in — which is what makes it usable as an audit trail
 * across three phones and a tablet that sync at different times.
 *
 * Votes whose proposal is absent from `proposals` are dropped: there is no
 * honest way to render "voted approve on <unknown>".
 */
export function decisionLog(
  proposals: readonly Proposal[],
  votes: readonly ProposalVote[],
  opts: DecisionLogOptions = {},
): DecisionLogEvent[] {
  const rolloverHour = opts.rolloverHour ?? DEFAULT_ROLLOVER_HOUR
  const byProposal = groupVotes(votes)
  const events: Omit<DecisionLogEvent, 'seq' | 'day'>[] = []

  for (const p of proposals) {
    const base = {
      proposalId: p.id,
      typeKey: p.typeKey,
      title: p.title,
      supersedesId: p.supersedesId,
    }

    events.push({
      ...base,
      at: p.createdAt,
      kind: 'proposed',
      actorId: p.createdBy,
      vote: null,
      comment: p.details,
      counterProposal: null,
      status: null,
      reason: null,
    })

    for (const v of byProposal.get(p.id) ?? []) {
      events.push({
        ...base,
        at: v.votedAt,
        kind: 'voted',
        actorId: v.voterId,
        vote: v.vote,
        comment: v.comment,
        counterProposal: v.counterProposal === null ? null : { ...v.counterProposal },
        status: null,
        reason: null,
      })
    }

    if (p.reminderSentAt !== null) {
      events.push({
        ...base,
        at: p.reminderSentAt,
        kind: 'reminded',
        actorId: null,
        vote: null,
        comment: null,
        counterProposal: null,
        status: null,
        reason: null,
      })
    }

    if (p.status !== 'pending' && p.decidedAt !== null) {
      events.push({
        ...base,
        at: p.decidedAt,
        kind: 'decided',
        actorId: decidingActor(
          p,
          byProposal.get(p.id) ?? [],
          opts.eligibleVoters,
          opts.proposerImplicitApprove ?? true,
        ),
        vote: null,
        comment: null,
        counterProposal: null,
        status: p.status,
        reason: p.decisionReason,
      })
    }
  }

  // A TOTAL order, so the log is byte-identical on every device however the
  // rows arrived. Stopping at (time, kind, proposal, actor, vote) would leave
  // two same-instant rows from one voter — which sync replay can produce —
  // ordered by array position, i.e. by whichever device is talking.
  events.sort(
    (a, b) =>
      a.at - b.at ||
      KIND_RANK[a.kind] - KIND_RANK[b.kind] ||
      compareStr(a.proposalId, b.proposalId) ||
      compareStr(a.actorId ?? '', b.actorId ?? '') ||
      compareStr(a.vote ?? '', b.vote ?? '') ||
      compareStr(a.comment ?? '', b.comment ?? '') ||
      compareStr(stableJson(a.counterProposal), stableJson(b.counterProposal)),
  )

  return events.map((e, i) => ({
    ...e,
    seq: i,
    day: rolloverDayOf(e.at, rolloverHour),
  }))
}

/**
 * Who caused the decision, where a person caused it at all.
 *
 * Derived from the recorded timestamps rather than stored, so it stays correct
 * if the decision is recomputed on another device. It reads the SAME effective
 * view of the votes that resolution does — eligibility filtered, first-vote-wins
 * applied, the proposer's implicit approve pinned at creation — because naming
 * someone whose vote resolution ignored would make the audit trail a lie.
 */
function decidingActor(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[] | undefined,
  proposerImplicit: boolean,
): MemberId | null {
  const decidedAt = proposal.decidedAt
  if (decidedAt === null) return null

  let wanted: Vote
  switch (proposal.decisionReason) {
    case 'cancelled_by_proposer':
      return proposal.createdBy
    case 'rejected_by_vote':
      wanted = 'reject'
      break
    case 'unanimous_approval':
      wanted = 'approve'
      break
    case 'auto_approved_timeout':
    case 'expired':
    case null:
      return null
  }

  // With no eligible list to go on, fall back to everyone who actually has a
  // row here plus the proposer, which is the best guess available.
  const eligible = new Set<string>(
    eligibleVoters ?? [
      proposal.createdBy,
      ...votes.filter(v => v.proposalId === proposal.id).map(v => v.voterId),
    ],
  )
  const eff = effectiveVotes(proposal, votes, eligible, proposerImplicit)

  let best: EffectiveVote | null = null
  for (const v of eff.values()) {
    if (v.vote !== wanted || v.at !== decidedAt) continue
    if (best === null) {
      best = v
    } else if (best.implicit !== v.implicit) {
      // A real tap outranks the proposer's implicit approve on the same instant.
      if (best.implicit) best = v
    } else if (compareStr(v.voterId, best.voterId) < 0) {
      best = v
    }
  }
  return best === null ? null : best.voterId
}

export interface DecisionLogQuery {
  proposalId?: ProposalId
  typeKey?: string
  actorId?: MemberId
  kind?: DecisionEventKind
  status?: ProposalStatus
  day?: DayKey
  /** Inclusive lower bound on `at`. */
  from?: number
  /** Exclusive upper bound on `at` — half-open, matching bookings. */
  to?: number
}

/** Filter the log. Preserves chronological order and the original `seq` values. */
export function queryDecisionLog(
  log: readonly DecisionLogEvent[],
  query: DecisionLogQuery = {},
): DecisionLogEvent[] {
  return log.filter(e => {
    if (query.proposalId !== undefined && e.proposalId !== query.proposalId) return false
    if (query.typeKey !== undefined && e.typeKey !== query.typeKey) return false
    if (query.actorId !== undefined && e.actorId !== query.actorId) return false
    if (query.kind !== undefined && e.kind !== query.kind) return false
    if (query.status !== undefined && e.status !== query.status) return false
    if (query.day !== undefined && e.day !== query.day) return false
    if (query.from !== undefined && e.at < query.from) return false
    if (query.to !== undefined && e.at >= query.to) return false
    return true
  })
}

/** Group a log by office day, days in chronological order. */
export function decisionLogByDay(
  log: readonly DecisionLogEvent[],
): { day: DayKey; events: DecisionLogEvent[] }[] {
  const out: { day: DayKey; events: DecisionLogEvent[] }[] = []
  for (const e of log) {
    const last = out[out.length - 1]
    if (last !== undefined && last.day === e.day) last.events.push(e)
    else out.push({ day: e.day, events: [e] })
  }
  return out
}

// ───────────────────────────────────────────────────────────────────────────
// 11. Per-proposal rollup
// ───────────────────────────────────────────────────────────────────────────

export interface ProposalSummary {
  proposal: Proposal
  outcome: ProposalOutcome
  /** Includes the proposer's implicit approve. */
  approvedBy: MemberId[]
  rejectedBy: MemberId[]
  awaiting: MemberId[]
  counterProposals: CounterProposal[]
  approvalsRequired: number
  rejectionsToBlock: number
}

export function summarise(
  proposal: Proposal,
  votes: readonly ProposalVote[],
  eligibleVoters: readonly MemberId[],
  now: number,
  opts: ResolveOptions = {},
): ProposalSummary {
  const eligible = uniqueIds(eligibleVoters)
  const eligibleSet = new Set<string>(eligible)
  const proposerImplicit = opts.proposerImplicitApprove ?? true
  const eff = effectiveVotes(proposal, votes, eligibleSet, proposerImplicit)
  const quorum: Quorum = opts.quorum ?? opts.types?.[proposal.typeKey]?.quorum ?? 'unanimous'

  const approvedBy: MemberId[] = []
  const rejectedBy: MemberId[] = []
  const awaiting: MemberId[] = []
  for (const id of eligible) {
    const v = eff.get(id)
    if (v === undefined) awaiting.push(id)
    else if (v.vote === 'approve') approvedBy.push(id)
    else rejectedBy.push(id)
  }

  return {
    proposal,
    outcome: resolve(proposal, votes, eligibleVoters, now, opts),
    approvedBy,
    rejectedBy,
    awaiting,
    counterProposals: counterProposalsOf(proposal, votes),
    approvalsRequired: requiredApprovals(quorum, eligible.length),
    rejectionsToBlock: blockingRejections(quorum, eligible.length),
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Internal helpers
// ───────────────────────────────────────────────────────────────────────────

function ascending(a: number, b: number): number {
  return a - b
}

function compareStr(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Deterministic serialisation, used only as a last-resort sort tie-break.
 * Keys are sorted, so two payloads that differ only in insertion order — which
 * they will, coming off two different devices — compare equal.
 */
function stableJson(v: unknown): string {
  if (v === null || v === undefined) return 'null'
  if (typeof v !== 'object') return JSON.stringify(v) ?? 'null'
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`
  const rec = v as Record<string, unknown>
  return `{${Object.keys(rec)
    .sort()
    .map(k => `${JSON.stringify(k)}:${stableJson(rec[k])}`)
    .join(',')}}`
}

function uniqueIds(ids: readonly MemberId[]): MemberId[] {
  const seen = new Set<string>()
  const out: MemberId[] = []
  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

function groupVotes(votes: readonly ProposalVote[]): Map<string, ProposalVote[]> {
  const out = new Map<string, ProposalVote[]>()
  for (const v of votes) {
    const arr = out.get(v.proposalId)
    if (arr === undefined) out.set(v.proposalId, [v])
    else arr.push(v)
  }
  return out
}
